import { decodeString, searchUrl } from "@findemdev/findem-helpers";
import { useCallback, useEffect, useState } from "react";

const JOB_TITLE_SEARCH_DELAY_MS = 500;
const MAX_JOB_TITLE_OPTIONS = 10;
const MIN_JOB_TITLE_SEARCH_LENGTH = 2;

export interface IProjectJobTitleOption {
  label: string;
  value: string;
}

interface ITitleSuggestion {
  current?: number;
  title?: string;
}

interface ITitleSuggestionsResponse {
  titles?: ITitleSuggestion[];
}

interface IUseProjectJobTitleOptionsArgs {
  visible: boolean;
}

const getTitleSuggestions = ({
  response,
}: {
  response: unknown;
}): ITitleSuggestion[] => {
  const value = response as ITitleSuggestionsResponse;
  return Array.isArray(value?.titles) ? value.titles : [];
};

const getJobTitleOptions = ({
  response,
  searchValue,
}: {
  response: unknown;
  searchValue: string;
}): IProjectJobTitleOption[] => {
  const titleSuggestions = getTitleSuggestions({ response });
  const options = [...titleSuggestions]
    .sort((left, right) => (right.current || 0) - (left.current || 0))
    .slice(0, MAX_JOB_TITLE_OPTIONS)
    .flatMap((suggestion) => {
      if (!suggestion.title || suggestion.current === 0) return [];

      const title = decodeString(suggestion.title);
      return [{ label: title, value: title }];
    });

  if (options.length === 0 && searchValue) {
    return [{ label: searchValue, value: searchValue }];
  }

  return options;
};

const fetchJobTitleOptions = async ({
  searchValue,
  signal,
}: {
  searchValue: string;
  signal?: AbortSignal;
}): Promise<unknown> => {
  const response = await fetch(`${searchUrl}/pub/api/query_suggest`, {
    body: JSON.stringify({
      count: 15,
      prefix: searchValue,
      types: "T",
    }),
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
    signal,
  });

  return response.json();
};

export const useProjectJobTitleOptions = ({
  visible,
}: IUseProjectJobTitleOptionsArgs) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<IProjectJobTitleOption[]>([]);
  const [searchValue, setSearchValue] = useState("");

  const resetOptions = useCallback(() => {
    setIsLoading(false);
    setOptions([]);
    setSearchValue("");
  }, []);

  const searchJobTitles = useCallback(
    ({ searchValue }: { searchValue: string }) => {
      setSearchValue(searchValue);
    },
    [],
  );

  useEffect(() => {
    if (!visible) {
      resetOptions();
    }
  }, [resetOptions, visible]);

  useEffect(() => {
    const trimmedSearchValue = searchValue.trim();

    if (!visible || trimmedSearchValue.length < MIN_JOB_TITLE_SEARCH_LENGTH) {
      setIsLoading(false);
      setOptions([]);
      return undefined;
    }

    const abortController = new AbortController();
    let isActive = true;
    setIsLoading(true);

    const timeout = setTimeout(async () => {
      try {
        const response = await fetchJobTitleOptions({
          searchValue: trimmedSearchValue,
          signal: abortController.signal,
        });

        if (isActive) {
          setOptions(
            getJobTitleOptions({
              response,
              searchValue: trimmedSearchValue,
            }),
          );
        }
      } catch {
        if (isActive) {
          setOptions([]);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }, JOB_TITLE_SEARCH_DELAY_MS);

    return () => {
      isActive = false;
      clearTimeout(timeout);
      abortController.abort();
    };
  }, [searchValue, visible]);

  return {
    isLoading,
    options,
    resetOptions,
    searchJobTitles,
  };
};
